/**
 * Push Notifications Service for Capacitor
 * Handles push notification registration, permissions, and event handling
 */

import { PushNotifications, Token, PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import { isNativePlatform, isIOS, isAndroid, getPlatform } from './platform';

export interface PushNotificationPayload {
  title?: string;
  body?: string;
  data?: Record<string, any>;
}

export interface PushNotificationCallbacks {
  onRegistration?: (token: string, platform: 'ios' | 'android') => void;
  onRegistrationError?: (error: Error) => void;
  onNotificationReceived?: (notification: PushNotificationPayload) => void;
  onNotificationAction?: (notification: PushNotificationPayload, actionId: string) => void;
}

let isInitialized = false;
let registeredCallbacks: PushNotificationCallbacks = {};

/**
 * Initialize push notifications
 * Must be called early in the app lifecycle
 */
export async function initializePushNotifications(callbacks: PushNotificationCallbacks = {}): Promise<void> {
  if (!isNativePlatform()) {
    console.log('Push notifications not available on web platform');
    return;
  }

  if (isInitialized) {
    console.log('Push notifications already initialized');
    return;
  }

  registeredCallbacks = callbacks;

  // Add listeners
  await addPushListeners();

  isInitialized = true;
  console.log('Push notifications initialized');
}

/**
 * Request push notification permission and register
 */
export async function requestPushPermission(): Promise<{ granted: boolean; token?: string }> {
  if (!isNativePlatform()) {
    return { granted: false };
  }

  try {
    // Check current permission status
    let permStatus = await PushNotifications.checkPermissions();

    if (permStatus.receive === 'prompt') {
      // Request permission
      permStatus = await PushNotifications.requestPermissions();
    }

    if (permStatus.receive !== 'granted') {
      console.log('Push notification permission denied');
      return { granted: false };
    }

    // Register with APNs/FCM
    if (!isPushRegistrationSafe(Capacitor.getPlatform())) {
      console.warn("[push] Android registration skipped — Firebase not configured");
      return { granted: true };
    }
    await PushNotifications.register();

    return { granted: true };
  } catch (error) {
    console.error('Error requesting push permission:', error);
    return { granted: false };
  }
}

/**
 * Check if push notifications are enabled
 */
export async function checkPushPermission(): Promise<boolean> {
  if (!isNativePlatform()) {
    return false;
  }

  try {
    const permStatus = await PushNotifications.checkPermissions();
    return permStatus.receive === 'granted';
  } catch (error) {
    console.error('Error checking push permission:', error);
    return false;
  }
}

/**
 * Get the list of delivered notifications
 */
export async function getDeliveredNotifications(): Promise<PushNotificationSchema[]> {
  if (!isNativePlatform()) {
    return [];
  }

  try {
    const { notifications } = await PushNotifications.getDeliveredNotifications();
    return notifications;
  } catch (error) {
    console.error('Error getting delivered notifications:', error);
    return [];
  }
}

/**
 * Remove specific delivered notifications
 */
export async function removeDeliveredNotifications(ids: string[]): Promise<void> {
  if (!isNativePlatform()) {
    return;
  }

  try {
    // Capacitor 8: notifications array now requires 'data' property
    await PushNotifications.removeDeliveredNotifications({
      notifications: ids.map(id => ({ id, data: {}, title: '', body: '' }))
    });
  } catch (error) {
    console.error('Error removing delivered notifications:', error);
  }
}

/**
 * Remove all delivered notifications
 */
export async function removeAllDeliveredNotifications(): Promise<void> {
  if (!isNativePlatform()) {
    return;
  }

  try {
    await PushNotifications.removeAllDeliveredNotifications();
  } catch (error) {
    console.error('Error removing all delivered notifications:', error);
  }
}

/**
 * Show a local notification (useful for foreground notifications)
 */
export async function showLocalNotification(
  title: string,
  body: string,
  data?: Record<string, any>,
): Promise<void> {
  if (!isNativePlatform()) {
    // On web, use browser notifications if available
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(title, { body });
    }
    return;
  }

  try {
    await LocalNotifications.schedule({
      notifications: [
        {
          id: Date.now(),
          title,
          body,
          extra: data,
          sound: 'default',
          attachments: undefined,
          actionTypeId: '',
          schedule: { at: new Date(Date.now() + 100) }, // Show immediately
        },
      ],
    });
  } catch (error) {
    console.error('Error showing local notification:', error);
  }
}

/**
 * Add push notification event listeners
 */
async function addPushListeners(): Promise<void> {
  // Registration success
  await PushNotifications.addListener('registration', (token: Token) => {
    console.log('Push registration success:', token.value);
    registeredCallbacks.onRegistration?.(token.value, getPlatform() as 'ios' | 'android');
  });

  // Registration error
  await PushNotifications.addListener('registrationError', (error) => {
    console.error('Push registration error:', error);
    registeredCallbacks.onRegistrationError?.(new Error(error.error));
  });

  // Notification received while app is in foreground
  await PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
    console.log('Push notification received:', notification);

    const payload: PushNotificationPayload = {
      title: notification.title,
      body: notification.body,
      data: notification.data,
    };

    registeredCallbacks.onNotificationReceived?.(payload);

    // Show local notification for foreground notifications
    if (notification.title || notification.body) {
      showLocalNotification(
        notification.title || 'InsuraCentral',
        notification.body || '',
        notification.data,
      );
    }
  });

  // User tapped on notification
  await PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
    console.log('Push notification action performed:', action);

    const payload: PushNotificationPayload = {
      title: action.notification.title,
      body: action.notification.body,
      data: action.notification.data,
    };

    registeredCallbacks.onNotificationAction?.(payload, action.actionId);
  });
}

/**
 * Remove all push notification listeners
 */
export async function removePushListeners(): Promise<void> {
  if (!isNativePlatform()) {
    return;
  }

  await PushNotifications.removeAllListeners();
  isInitialized = false;
}

/**
 * Get the badge count (iOS only)
 */
export async function getBadgeCount(): Promise<number> {
  if (!isIOS()) {
    return 0;
  }

  try {
    // Badge count is typically managed through local notifications on Capacitor
    return 0; // Placeholder - implement if needed
  } catch (error) {
    console.error('Error getting badge count:', error);
    return 0;
  }
}

/**
 * Set the badge count (iOS only)
 */
export async function setBadgeCount(count: number): Promise<void> {
  if (!isIOS()) {
    return;
  }

  try {
    // Badge is set through push notifications or local notifications
    // This is a placeholder - implement through your push service
    console.log('Setting badge count:', count);
  } catch (error) {
    console.error('Error setting badge count:', error);
  }
}

/**
 * Clear the badge count (iOS only)
 */
export async function clearBadge(): Promise<void> {
  await setBadgeCount(0);
}

// Android push needs Firebase (FCM): without google-services.json in the
// native project, PushNotifications.register() throws NATIVE-side
// ("Default FirebaseApp is not initialized") on a plugin thread — a JS
// try/catch cannot contain it and the whole app crash-loops after login.
// Gate registration off on Android until Firebase is wired in
// (NEXT_PUBLIC_ANDROID_PUSH_READY=true after adding google-services.json).
export function isPushRegistrationSafe(platform: string): boolean {
  if (platform !== "android") return true;
  return process.env.NEXT_PUBLIC_ANDROID_PUSH_READY === "true";
}
